import {Action, Button, Card, DataStatus, TabProps, Tabs, useDialog} from 'platform/components';
import {Box, FlexboxJustify, Hide, HStack, Show, Space} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {always, isNil, mergeAll, uniqBy, values} from 'ramda';
import {isNilOrEmpty, isNonEmptyArray, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetNoteQuery, useGetParticipationQuery, useUpdateNoteMutation} from '@dms/api/core';
import {useGetCustomerV2Query, useListCustomerContractInformationsQuery} from '@dms/api/customer';
import {
  CheckoutContractInformationBody,
  CheckoutSubEntityClass,
  OrderPaymentResponseBody,
  useGetBusinessCaseQuery,
  useGetOrderQuery,
  useGetVehicleQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  DOCUMENT_ACTION_COLUMNS,
  useGetDocumentDatagridActions,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  handleApiError,
  queryParams,
  refreshDmsDatagrid,
  UploadDocumentsDialog,
  usePermissions,
  usePricePermissions,
  useTenant,
} from '@dms/shared';
import {
  FileDataTypeEnum,
  NoteRequestBody,
  OrderDiscriminatorEnum,
  PaymentStateEnum,
} from '@dms/teas';

import {
  buildArray,
  Nullish,
  suffixTestId,
  TestIdProps,
  useQueryState,
  useRequiredParams,
} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {CheckoutOrderBilling} from './Billing/CheckoutOrderBilling';
import {Documents} from './Documents';
import {useGetCheckoutDocumentTemplates} from './hooks/useGetCheckoutDocumentTemplates';
import {CheckoutOrderPayment} from './Payment/CheckoutOrderPayments';
import {TAB_IDS} from './tabIds';
import {CheckoutOrderProps} from './types/CheckoutOrderProps';

const TAB_IDS_VALUES = values(TAB_IDS);
const GRID_REFRESH_TIMEOUT = 2000;

export const CheckoutOrder = ({
  allOrders,
  documents,
  isBrokeredSale,
  isBrokerage,
  handleCreateDocuments,
  refreshDocumentContext,
  isLoadingDocuments,
  invoicable,
  shouldHidePaymentTab,
  ...props
}: CheckoutOrderProps & TestIdProps) => {
  const {tenantCountry, isTenantCountryRestricted} = useTenant();
  const {id: businessCaseId} = useRequiredParams();
  const {data: order} = useGetOrderQuery({orderId: props.order.id, checkoutId: props.checkoutId});
  const {data: customer} = useGetCustomerV2Query({customerId: props.customerId});
  const {data: customerContractInformation} = useListCustomerContractInformationsQuery({
    customerId: props.customerId,
  });

  const {data: businessCase} = useGetBusinessCaseQuery({businessCaseId});
  const isPurchaseBusinessCase =
    businessCase?.businessCaseType === 'BUYING' ||
    businessCase?.businessCaseType === 'PURCHASE_BROKERAGE';
  const isSaleBusinessCase = businessCase?.businessCaseType === 'SELLING';

  const vehicleId = match<[boolean, boolean], string | Nullish>([
    isPurchaseBusinessCase,
    isSaleBusinessCase,
  ])
    .with([true, false], always(businessCase?.offers?.[0]?.purchaseVehicles?.[0]?.vehicleId))
    .with([false, true], always(businessCase?.offers?.[0]?.saleVehicles?.[0]?.vehicleId))
    .otherwise(always(null));

  const {data: vehicleData} = useGetVehicleQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: isNil(vehicleId)}
  );

  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: vehicleId ?? '',
    },
    {skip: isNilOrEmpty(vehicleId)}
  );

  const {data: businessCaseParticipation} = useGetParticipationQuery({
    recordId: businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });

  const [
    canReadPaymentInformation,
    canGenerateSaleDocuments,
    canGeneratePurchaseDocuments,
    vehicleEnableLeasingAndCreditCompany,
    vehicleDisableLeasingAndCreditCompany,
    vehicleSetLeasingAndCreditCompany,
    vehicleSetFleetInsurance,
    vehicleUpdateStorageSpace,
    canReadDocuments,
  ] = usePermissions({
    permissionKeys: [
      'readCheckoutPaymentInformation',
      'businessCaseSaleGenerateDocuments',
      'generateBusinessCasePurchaseDocuments',
      'vehicleEnableLeasingAndCreditCompany',
      'vehicleDisableLeasingAndCreditCompany',
      'vehicleSetLeasingAndCreditCompany',
      'vehicleSetFleetInsurance',
      'vehicleUpdateStorageSpace',
      'readDocuments',
    ],
    scopes: {
      vehicleEnableLeasingAndCreditCompany: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
      vehicleDisableLeasingAndCreditCompany: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
      vehicleSetLeasingAndCreditCompany: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
      vehicleSetFleetInsurance: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
      vehicleUpdateStorageSpace: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
      businessCaseSaleGenerateDocuments: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      generateBusinessCasePurchaseDocuments: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });

  const canEditSaleVehicle =
    vehicleEnableLeasingAndCreditCompany &&
    vehicleDisableLeasingAndCreditCompany &&
    vehicleSetLeasingAndCreditCompany &&
    vehicleSetFleetInsurance &&
    vehicleUpdateStorageSpace;

  const {canReadVehicleSellingPrice, canReadVehiclePurchasePrice} = usePricePermissions({
    vehicleRecordId: vehicleId,
    businessCaseRecordId: null,
  });

  const [activeTabId, setActiveTabId] = useQueryState(
    `${props.order.orderDiscriminator}-${queryParams.BUSINESS_CASE_CHECKOUT_ORDER}`
  );

  const isFirstTabActive = isNil(activeTabId) || activeTabId === TAB_IDS.BILLING_INFORMATION;
  const isLastTabActive = activeTabId === TAB_IDS.PAYMENT;
  const isDocumentTabActive = activeTabId === TAB_IDS.DOCUMENTS && canReadDocuments;

  const moveToNextTab = () => {
    const activeTabIndex = isNotNil(activeTabId)
      ? TAB_IDS_VALUES.findIndex((item) => item === activeTabId)
      : 0;

    if (!canReadDocuments && isFirstTabActive) {
      setActiveTabId(TAB_IDS.PAYMENT);
      return;
    }

    if (activeTabIndex === TAB_IDS_VALUES.length) {
      return;
    }

    setActiveTabId(TAB_IDS_VALUES[activeTabIndex + 1]);
  };

  const moveToPreviousTab = () => {
    const activeTabIndex = TAB_IDS_VALUES.findIndex((item) => item === activeTabId);
    if (activeTabIndex === 0) {
      return;
    }

    if (!canReadDocuments && isLastTabActive) {
      setActiveTabId(TAB_IDS.BILLING_INFORMATION);
      return;
    }

    setActiveTabId(TAB_IDS_VALUES[activeTabIndex - 1]);
  };

  const refreshDataGrid = () => refreshDmsDatagrid('document-context-list-business-case-checkout');

  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();

  const {data: purchaseContract} = useGetNoteQuery({
    resourceId: EntityResourceIds.checkoutOrder,
    recordId: props.order.id,
    context: 'PURCHASE_CONTRACT',
  });
  const {data: purchaseBrokerageContract} = useGetNoteQuery({
    resourceId: EntityResourceIds.checkoutOrder,
    recordId: props.order.id,
    context: 'PURCHASE_BROKERAGE_CONTRACT',
  });
  const {data: saleContract} = useGetNoteQuery({
    resourceId: EntityResourceIds.checkoutOrder,
    recordId: props.order.id,
    context: 'SALE_CONTRACT',
  });
  const {data: saleBrokerageContract} = useGetNoteQuery({
    resourceId: EntityResourceIds.checkoutOrder,
    recordId: props.order.id,
    context: 'SALE_BROKERAGE_CONTRACT',
  });

  const [updateDocumentNote] = useUpdateNoteMutation();

  const documentNotes = {
    'purchase-contract': purchaseContract,
    'purchase-brokerage-contract': purchaseBrokerageContract,
    'sale-contract': saleContract,
    'sale-brokerage-contract': saleBrokerageContract,
  };

  const {templates, isLoading, isError} = useGetCheckoutDocumentTemplates({
    isBrokeredSale: isBrokeredSale ?? false,
    order,
  });

  const isBrokerageFees =
    order?.orderDiscriminator === OrderDiscriminatorEnum.PURCHASE_BROKERAGE_FEES;

  const hasAllDocumentsGenerated: boolean =
    isNonEmptyArray(documents) &&
    uniqBy((template) => template.type, templates ?? [])?.every((template) =>
      documents?.some((document) => template.type === document.documentType)
    );

  const isAtLeastOneDocumentGenerated = documents?.some(
    (document) => document.documentType !== FileDataTypeEnum.GDPR_MARKETING_CONSENT
  );

  const paymentTabIcon = order?.payments?.every(isPaymentPaid) ? 'success' : 'pending';

  const documentTabIcon: TabProps['icon'] =
    hasAllDocumentsGenerated || isAtLeastOneDocumentGenerated ? 'success' : 'pending';

  const justifyNav = match<[boolean, boolean], FlexboxJustify>([isFirstTabActive, isLastTabActive])
    .with([true, Pattern.any], always('flex-end'))
    .with([false, true], always('flex-start'))
    .otherwise(always('space-between'));

  const handleNoteChange = async (
    subEntityClass: CheckoutSubEntityClass,
    noteRequestBody: NoteRequestBody
  ) => {
    await updateDocumentNote({
      resourceId: EntityResourceIds.checkoutOrder,
      recordId: props.order.id,
      context: subEntityClass,
      updateNoteRequestBody: {text: noteRequestBody.note},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const [actions, bulkSignatureAction] = useGetDocumentDatagridActions({
    refreshData: refreshDataGrid,
    customerId: customer?.id,
    contextTarget: 'business-case',
    contextId: businessCaseId,
    vehicleId,
  });

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {businessCaseId: props.documentContextId ?? ''}]);

  const isDocumentUploadSupported = isNotNil(props.documentContextTarget);

  const onDocumentCreated = () => {
    // TODO T20-34493
    // refreshDataGrid();
    refreshDocumentContext?.();

    // TODO ugly temporary fix with timeout to show document print icon
    setTimeout(refreshDataGrid, GRID_REFRESH_TIMEOUT);
  };

  const tabs = buildArray<TabProps>()
    .when(isNotNil(order?.contractInformation) && isNotNilOrEmpty(customerContractInformation), {
      id: TAB_IDS.BILLING_INFORMATION,
      title: i18n.t('entity.checkout.labels.billingInformation'),
      'data-testid': suffixTestId('billingInformation', props),
      icon: 'success',
      isDisabled: props.disabledTabIds?.includes('BILLING_INFORMATION'),
      content: (
        <CheckoutOrderBilling
          {...props}
          businessCase={businessCase}
          customerContractInformation={customerContractInformation!}
          checkoutContractsInformation={
            order?.contractInformation as CheckoutContractInformationBody
          }
          data-testid={suffixTestId('billingInformation', props)}
        />
      ),
    })
    .when(canReadDocuments && !isBrokerageFees, {
      id: TAB_IDS.DOCUMENTS,
      title: i18n.t('entity.checkout.labels.documents'),
      icon: documentTabIcon,
      isDisabled: props.disabledTabIds?.includes('DOCUMENTS'),
      'data-testid': suffixTestId('documents', props),
      content: (
        <>
          <DataStatus isLoading={isLoading} isError={isError}>
            <Documents
              emptySubHeadline={i18n.t('entity.businessCase.labels.allIssuedDocuments')}
              handleCreateDocuments={handleCreateDocuments(props.order.id, onDocumentCreated)}
              isCreateDisabled={
                order?.orderDiscriminator === OrderDiscriminatorEnum.PURCHASE ||
                order?.orderDiscriminator === OrderDiscriminatorEnum.PURCHASE_BROKERAGE_FEES ||
                order?.orderDiscriminator === OrderDiscriminatorEnum.PURCHASE_BROKERAGE_SALE
                  ? !canGeneratePurchaseDocuments
                  : !canGenerateSaleDocuments
              }
              isLoading={isLoadingDocuments}
              onNoteChange={handleNoteChange}
              documentNotes={documentNotes}
              documents={documents}
              businessCase={businessCase}
              templates={templates}
              {...props}
              data-testid={suffixTestId('documents', props)}
            />
          </DataStatus>
          <Show when={isNotNil(props.documentContextId)}>
            <Space vertical={4} />
            <Show when={isDocumentUploadSupported}>
              <UploadDocumentsDialog
                isOpen={isUploadDialogOpen}
                handleClose={closeUploadDialog}
                onDocumentCreated={onDocumentCreated}
                contextTarget={props.documentContextTarget!}
                contextId={props.documentContextId!}
                bulkContext={props.documentBulkContext}
              />
            </Show>
            <Card
              title={i18n.t('page.businessCase.documents.title')}
              actions={buildArray<Action>()
                .when(isDocumentUploadSupported, {
                  type: 'button',
                  variant: 'link',
                  leftIcon: 'action/backup',
                  title: i18n.t('entity.document.actions.uploadDocument'),
                  onClick: openUploadDialog,
                })
                .add(...bulkSignatureAction)}
              variant="inlineWhite"
            >
              <Box minHeight={40} key={activeTabId}>
                <DmsDataGrid
                  autoHeight
                  gridCode="document-context-list-business-case-checkout"
                  predefinedColumns={DOCUMENT_ACTION_COLUMNS}
                  actionCallback={actions}
                  queryModifier={queryModifier}
                  data-testid={suffixTestId('dataGrid', props)}
                />
              </Box>
            </Card>
          </Show>
        </>
      ),
    })
    .when(
      !isTenantCountryRestricted(['POL']) &&
        invoicable &&
        canReadPaymentInformation &&
        !shouldHidePaymentTab &&
        ((isSaleBusinessCase && canReadVehicleSellingPrice) ||
          (isPurchaseBusinessCase && canReadVehiclePurchasePrice)),
      {
        id: TAB_IDS.PAYMENT,
        title: i18n.t('entity.checkout.labels.payment'),
        icon: paymentTabIcon,
        isDisabled: props.disabledTabIds?.includes('PAYMENT'),
        'data-testid': suffixTestId('payment', props),
        content: (
          <CheckoutOrderPayment
            allOrders={allOrders}
            tenantCountry={tenantCountry}
            isBrokeredSale={isBrokeredSale}
            isBrokerage={isBrokerage}
            {...props}
            data-testid={suffixTestId('payment', props)}
            isFleetInsuranceDisabled={!canEditSaleVehicle}
          />
        ),
      }
    );

  const isActiveTabIdValid = activeTabId && tabs.some((tab) => tab.id === activeTabId);

  return (
    <Show when={isNotNilOrEmpty(tabs)}>
      <Tabs
        data-testid={props['data-testid']}
        variant="condensed"
        onChange={setActiveTabId}
        activeTabId={isActiveTabIdValid ? activeTabId : tabs[0]?.id}
        tabs={tabs}
      />

      <HStack justify={justifyNav}>
        <Show when={!isFirstTabActive}>
          <Button
            data-testid={suffixTestId('back', props)}
            variant="outlined"
            onClick={moveToPreviousTab}
            leftIcon="navigation/chevron_left"
            title={i18n.t('general.actions.back')}
          />
        </Show>
        <Hide
          when={
            isLastTabActive ||
            (isDocumentTabActive && isTenantCountryRestricted(['POL'])) ||
            (isFirstTabActive && !canReadDocuments && !canReadVehicleSellingPrice) ||
            (isDocumentTabActive && canReadDocuments && !canReadVehicleSellingPrice)
          }
        >
          <Button
            data-testid={suffixTestId('continue', props)}
            onClick={moveToNextTab}
            variant="outlined"
            rightIcon="navigation/chevron_right"
            title={
              isFirstTabActive && !isBrokerageFees && canReadDocuments
                ? i18n.t('entity.businessCase.continueToDocuments')
                : i18n.t('entity.businessCase.continueToPayment')
            }
          />
        </Hide>
      </HStack>
    </Show>
  );
};

const isPaymentPaid = (payment: OrderPaymentResponseBody) =>
  payment.paymentState === PaymentStateEnum.PAID;
